import React, { useState, useEffect } from 'react';
import { ordersAPI } from '../../services/api';
import { ClockIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

interface OTPDisplayProps {
  orderId: string;
  orderStatus: string;
}

const OTPDisplay: React.FC<OTPDisplayProps> = ({ orderId, orderStatus }) => {
  const [otp, setOtp] = useState<{ otp_code: string; expiry_time: string; status: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Only fetch OTP for orders in active delivery status
    const activeStatuses = ['accepted', 'assigned', 'picked_up', 'in_transit'];
    if (activeStatuses.includes(orderStatus)) {
      fetchOTP();
    }
  }, [orderId, orderStatus]);

  const fetchOTP = async () => {
    try {
      setLoading(true);
      const otpData = await ordersAPI.getOrderOTP(orderId);
      setOtp(otpData);
    } catch (err: any) {
      setError('Unable to load OTP');
    } finally {
      setLoading(false);
    }
  };

  const formatExpiryTime = (expiryTime: string) => {
    const expiry = new Date(expiryTime);
    const now = new Date();
    const hoursLeft = Math.floor((expiry.getTime() - now.getTime()) / (1000 * 60 * 60));
    
    if (hoursLeft < 0) return 'Expired';
    if (hoursLeft < 1) return 'Expires soon';
    return `Valid for ${hoursLeft} hours`;
  };

  const activeStatuses = ['accepted', 'assigned', 'picked_up', 'in_transit'];
  if (!activeStatuses.includes(orderStatus)) {
    return null;
  }

  if (loading) {
    return (
      <div className="px-6 py-3 bg-volexx-blue-50 border-t border-volexx-blue-100">
        <div className="animate-pulse flex items-center">
          <div className="h-4 w-24 bg-volexx-blue-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !otp) {
    return null;
  }

  if (otp.status === 'used') {
    return (
      <div className="px-6 py-3 bg-green-50 border-t border-green-100">
        <div className="flex items-center text-green-800">
          <ShieldCheckIcon className="h-5 w-5 mr-2" />
          <span className="text-sm font-medium">Delivery confirmed with OTP</span>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-4 bg-volexx-blue-50 border-t border-volexx-blue-100">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center mb-2">
            <ShieldCheckIcon className="h-5 w-5 text-volexx-blue-600 mr-2" />
            <h4 className="text-sm font-semibold text-volexx-charcoal-700">
              Delivery Confirmation OTP
            </h4>
          </div>
          <p className="text-xs text-volexx-charcoal-600 mb-3">
            Share this OTP with your rider to confirm delivery
          </p>
          <div className="flex items-center space-x-4">
            <div className="bg-white px-6 py-3 rounded-volexx border-2 border-volexx-blue-500">
              <div className="text-2xl font-bold text-volexx-blue-600 tracking-widest">
                {otp.otp_code}
              </div>
            </div>
            <div className="flex items-center text-xs text-volexx-charcoal-600">
              <ClockIcon className="h-4 w-4 mr-1" />
              {formatExpiryTime(otp.expiry_time)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPDisplay;
